import { createId } from "@paralleldrive/cuid2";

/**
 * Zod's own `cuid2` check, copied rather than imported.
 *
 * ⚠️ IT IS NOT THE SAME RULE AS `@paralleldrive/cuid2`'s `isCuid`. The package's validator accepts a
 * leading digit; zod 3.22's `cuid2Regex` does not. The rule that matters here is the one the API
 * rejects writes with, which is zod's — validating against the library instead would call a document
 * healthy and then watch the server refuse it.
 */
const CUID2 = /^[a-z][a-z0-9]*$/;

const isValidId = (value: unknown) => typeof value === "string" && CUID2.test(value);

type Items = { items?: unknown };

/**
 * Give every item with an unusable id a fresh one, in place, and report how many were replaced.
 *
 * WHY THIS EXISTS. `idSchema` is `z.string().cuid2()`, applied to every item in every section, and
 * `PATCH /resume/:id` validates the WHOLE document. So one malformed id — from an importer, an older
 * build, or a hand-edited row — does not degrade anything gracefully: it returns
 * `400 Validation failed` for every subsequent write to that résumé, permanently. The student's
 * symptom is "saving does nothing", on a résumé that renders perfectly.
 *
 * `uploaded-resume.ts` generated exactly such ids (see the note on its `createId`), and fixing the
 * generator does nothing for the résumés it already created. This is the repair for those: run on
 * load, so the next save carries valid ids and succeeds.
 *
 * ⚠️ SAFE TO REWRITE BECAUSE ITEM IDS ARE NOT REFERENCED. `metadata.layout` holds SECTION keys, and
 * the coach optimizer's `fieldPath`s are positional (`sections.skills.items[3].name`) — nothing
 * anywhere points at an item by id. Custom SECTION ids are deliberately left alone: those DO appear
 * in `layout` as `custom.<id>`, so renaming one means moving the layout entry with it, and they come
 * from `createId()` in the résumé store, so they are not a source of bad values.
 *
 * Deliberately NOT a save. Repairing on load and immediately writing back would mean opening a
 * résumé mutates it, including for someone who only came to look at it. The ids are fixed in the
 * store; the next real edit persists them, which is the same moment the student would have noticed
 * the failure.
 */
export const repairResumeItemIds = (data: unknown): number => {
  if (!data || typeof data !== "object") return 0;

  let repaired = 0;

  const fixCollection = (collection: unknown) => {
    const items = (collection as Items | null)?.items;
    if (!Array.isArray(items)) return;

    for (const item of items) {
      if (!item || typeof item !== "object") continue;

      const entry = item as { id?: unknown };
      if (isValidId(entry.id)) continue;

      entry.id = createId();
      repaired += 1;
    }
  };

  const sections = (data as { sections?: Record<string, unknown> }).sections;

  for (const [key, section] of Object.entries(sections ?? {})) {
    // `custom` is a MAP of sections, not a section — its own `items` key does not exist, and its
    // values are what carry items.
    if (key === "custom") {
      for (const customSection of Object.values(
        (section as Record<string, unknown> | null) ?? {},
      )) {
        fixCollection(customSection);
      }
      continue;
    }

    fixCollection(section);
  }

  // Same `cuid2` constraint, same consequence, and easy to miss because these are not "items".
  fixCollection({ items: (data as { basics?: { customFields?: unknown } }).basics?.customFields });

  return repaired;
};
